import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useAccessibility } from "@/context/AccessibilityContext";
import OakLogo from "@/components/OakLogo";
import FocusStreak from "@/components/FocusStreak";
import StreakBadge from "@/components/StreakBadge";
import XPBadge from "@/components/XPBadge";
import { useT } from "@/hooks/useT";
import { LANGUAGES } from "@/components/LanguagePersonaBar";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/posthog";
import {
  Coins, LogOut, CreditCard, Zap, Home, Menu, X,
  GraduationCap, CalendarCheck2, Library as LibraryIcon, UserPlus, User, Globe,
  DollarSign, Settings as SettingsIcon, Eye, Type, Gift, ShieldCheck,
  ChevronDown, ChevronRight, BookOpen, PlayCircle,
} from "lucide-react";

const LOW_THRESHOLD = 2;

const MENU_ITEMS = [
  { to: "/academy", key: "nav_academy", Icon: GraduationCap },
  { to: "/planner", key: "nav_planner", Icon: CalendarCheck2 },
  { to: "/library", key: "nav_library", Icon: LibraryIcon },
  { to: "/settings", key: "nav_profile", Icon: User },
];

// Default handler: fire the global oak:open-upgrade event so the singleton
// PricingModal (mounted in App.js via DailyLimitGate) opens on any page,
// without every page having to pass an onOpenCheckout prop.
const dispatchUpgrade = () => {
  try {
    trackEvent("upgrade_clicked", { source: "header", surface: "default_handler" });
    window.dispatchEvent(new Event("oak:open-upgrade"));
  } catch {}
};

export default function Header({ credits, daysUntilReset: daysUntilResetProp, weeklyFloor: weeklyFloorProp, onOpenAd, onOpenCheckout }) {
  const openUpgrade = onOpenCheckout || dispatchUpgrade;
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useT();
  const { dyslexia, focusMode, setDyslexia, setFocusMode } = useAccessibility();
  const isLow = typeof credits === "number" && credits < LOW_THRESHOLD;
  // Cycle metadata. Falls back to a self-fetch when not passed by parent so
  // consumer pages don't all need to thread it through. Updates whenever
  // the credits prop changes (post-purchase, post-spend, post-ad reward).
  const [cycle, setCycle] = useState({
    daysUntilReset: typeof daysUntilResetProp === "number" ? daysUntilResetProp : null,
    weeklyFloor: typeof weeklyFloorProp === "number" ? weeklyFloorProp : 2,
  });
  useEffect(() => {
    if (!user) return;
    if (typeof daysUntilResetProp === "number") return;
    api.get("/credits/balance").then(({ data }) => {
      setCycle({
        daysUntilReset: typeof data?.days_until_reset === "number" ? data.days_until_reset : null,
        weeklyFloor: typeof data?.weekly_floor === "number" ? data.weekly_floor : 2,
      });
    }).catch(() => {});
  }, [user, credits, daysUntilResetProp]);
  const daysUntilReset = cycle.daysUntilReset;
  const weeklyFloor = cycle.weeklyFloor;
  // CPX Research surveys — "Earn Credits" CTAs hide entirely when CPX
  // is not configured (no app_id / secure_hash) or the user has hit
  // their daily survey cap.
  const [rewardsEnabled, setRewardsEnabled] = useState(true);
  useEffect(() => {
    if (!user) return;
    api.get("/cpx/status")
      .then(({ data }) => setRewardsEnabled(Boolean(data?.enabled) && (data?.remaining_today ?? 0) > 0))
      .catch(() => setRewardsEnabled(true)); // optimistic: fail-open until /cpx/status answers
  }, [user]);
  const showCountdown =
    typeof daysUntilReset === "number" &&
    daysUntilReset >= 0 &&
    typeof credits === "number" &&
    typeof weeklyFloor === "number" &&
    credits < weeklyFloor;
  const countdownLabel =
    daysUntilReset === 0 ? `+${weeklyFloor - credits} today` :
    daysUntilReset === 1 ? `+${weeklyFloor - credits} tomorrow` :
    `+${weeklyFloor - credits} in ${daysUntilReset}d`;
  const [menuOpen, setMenuOpen] = useState(false);
  // "My Courses" expandable section inside the hamburger. We lazy-fetch
  // the user's path list the FIRST time the user expands it to avoid
  // hitting the API on every page mount. Cached for the session.
  const [myCoursesOpen, setMyCoursesOpen] = useState(false);
  const [myCoursesLoading, setMyCoursesLoading] = useState(false);
  const [myCourses, setMyCourses] = useState(null);  // null = not loaded yet
  const expandMyCourses = async () => {
    const next = !myCoursesOpen;
    setMyCoursesOpen(next);
    if (next && myCourses === null) {
      setMyCoursesLoading(true);
      try {
        const { data } = await api.get("/counselor/paths");
        setMyCourses(Array.isArray(data) ? data : []);
      } catch {
        setMyCourses([]);
      } finally {
        setMyCoursesLoading(false);
      }
    }
  };
  const [adminStats, setAdminStats] = useState(null); // {is_admin, today_inr, today_orders}
  // "Continue where you left off" — most recently viewed path + a coarse
  // progress estimate. Fetched once when the menu first opens; refresh
  // happens automatically next open. Null = not yet loaded; `false` =
  // user has no paths (204 from /counselor/resume).
  const [resume, setResume] = useState(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Lazy-fetch the resume target whenever the menu opens. Refresh on each
  // open so the "Continue where you left off" tile always reflects the
  // most recent visit (cheap endpoint — small payload).
  useEffect(() => {
    if (!menuOpen || !user) return;
    let cancelled = false;
    api.get("/counselor/resume")
      .then(({ data, status }) => {
        if (cancelled) return;
        // 204 No Content → user has no paths yet.
        if (status === 204 || !data || !data.path_id) {
          setResume(false);
          return;
        }
        setResume(data);
      })
      .catch(() => { if (!cancelled) setResume(false); });
    return () => { cancelled = true; };
  }, [menuOpen, user]);

  // Admin-only: fetch today's revenue. Cached via localStorage so the pill
  // is visible on first paint for repeat admin visits, then refreshed once.
  useEffect(() => {
    if (!user) return;
    try {
      const cached = localStorage.getItem("oak_admin_stats");
      if (cached) setAdminStats(JSON.parse(cached));
    } catch {}
    api.get("/admin/whoami").then(({ data }) => {
      if (data?.is_admin) {
        const next = {
          is_admin: true,
          today_inr: data.today_inr ?? 0,
          today_orders: data.today_orders ?? 0,
        };
        setAdminStats(next);
        try { localStorage.setItem("oak_admin_stats", JSON.stringify(next)); } catch {}
      } else {
        setAdminStats({ is_admin: false });
        try { localStorage.removeItem("oak_admin_stats"); } catch {}
      }
    }).catch(() => {});
  }, [user]);

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-2xl bg-[#0B0E14]/80 border-b border-white/10"
      data-testid="app-header"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-10 py-3 md:py-4 flex items-center justify-between gap-3">
        {/* LEFT — brand + home */}
        <div className="flex items-center gap-2 md:gap-3">
          <Link to="/" className="flex items-center gap-2 md:gap-3 group" data-testid="header-brand">
            <OakLogo size={32} glow />
            <div className="leading-tight hidden sm:block">
              <div className="font-display font-semibold text-[16px] tracking-tight">Oak AI Campus</div>
              <div className="label-eyebrow !text-[9px]">{t("header_brand_tagline")}</div>
            </div>
          </Link>
          <Link
            to="/"
            className="hidden md:inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 hover:border-white/20 text-white/70 hover:text-white transition"
            title={t("nav_home")}
            data-testid="btn-home"
          >
            <Home size={16} />
          </Link>
        </div>

        {/* RIGHT — language, actions, dropdown.
            `min-w-0` allows the flex children to shrink (prevents the
            row from overflowing the viewport on narrow Android — Realme
            GT 7 reported the hamburger getting pushed past the right
            edge Apr 28). */}
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          {/* Accessibility toggles — hidden below sm to keep the row
              compact on small Android phones; users can still flip them
              from the hamburger menu (which is `shrink-0` and always shows). */}
          <button
            onClick={() => {
              const next = !dyslexia;
              setDyslexia(next);
              trackEvent("accessibility_toggle", { feature: "opendyslexic", enabled: next });
            }}
            className={`hidden sm:grid h-9 w-9 place-items-center rounded-full border transition ${
              dyslexia
                ? "border-[#00FF9D]/60 text-[#00FF9D] bg-[#00FF9D]/10"
                : "border-white/10 text-white/55 hover:text-white hover:border-white/30"
            }`}
            title={dyslexia ? "Disable OpenDyslexic" : "Enable OpenDyslexic (reader-friendly font)"}
            aria-pressed={dyslexia}
            aria-label="Toggle OpenDyslexic mode"
            data-testid="btn-toggle-dyslexia"
          >
            <Type size={15} />
          </button>
          <button
            onClick={() => {
              const next = !focusMode;
              setFocusMode(next);
              trackEvent("accessibility_toggle", { feature: "focus_mode", enabled: next });
            }}
            className={`hidden sm:grid h-9 w-9 place-items-center rounded-full border transition ${
              focusMode
                ? "border-[#8aa8ff]/60 text-[#8aa8ff] bg-[#8aa8ff]/10"
                : "border-white/10 text-white/55 hover:text-white hover:border-white/30"
            }`}
            title={focusMode ? "Exit Focus Mode" : "Enter Focus Mode (hides distractions)"}
            aria-pressed={focusMode}
            aria-label="Toggle Focus Mode"
            data-testid="btn-toggle-focus"
          >
            <Eye size={15} />
          </button>

          {/* Language selector */}
          <div className="relative flex items-center">
            <Globe size={14} className="absolute left-2.5 text-white/45 pointer-events-none" />
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="appearance-none bg-black/40 border border-white/10 rounded-full pl-7 pr-3 py-1.5 text-xs text-white/80 focus:outline-none focus:border-[#0044FF]/60 max-w-[88px] sm:max-w-[130px] md:max-w-none truncate"
              data-testid="header-lang-select"
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code} className="bg-[#0B0E14]">
                  {l.label}
                </option>
              ))}
            </select>
          </div>

          {/* Wallet pill */}
          <StreakBadge />
          <XPBadge />
          <div
            className={`flex items-center gap-2 rounded-full pl-3 pr-1.5 py-1 text-sm transition ${
              isLow
                ? "glass !rounded-full border-[#FF7B00]/40 bg-[#FF7B00]/10 glow-mint"
                : "glass !rounded-full"
            }`}
            data-testid="wallet-credits-display"
          >
            <Coins size={14} className={isLow ? "text-[#ffb067]" : "text-[#00FF9D]"} />
            <span className="font-display font-semibold text-sm">{credits ?? 0}</span>
            <span className="text-white/50 text-[11px] hidden md:inline">
              {t("header_credits")}
              {credits === 1 ? "" : "s"}
            </span>
            {showCountdown && (
              <span
                className="ml-1 inline-flex items-center gap-1 rounded-full bg-[#00FF9D]/15 border border-[#00FF9D]/30 px-2 py-0.5 text-[9px] font-mono-oak text-[#00FF9D] tracking-wider"
                title={`Free credits top up to ${weeklyFloor} every 7 days`}
                data-testid="wallet-topup-countdown"
              >
                {countdownLabel}
              </span>
            )}
            {isLow && (
              <button
                onClick={openUpgrade}
                className="ml-1 flex sm:hidden items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-display font-semibold btn-primary"
                data-testid="btn-upgrade-wallet"
              >
                <Zap size={10} /> {t("header_upgrade")}
              </button>
            )}
          </div>

          {/* Persistent Upgrade button — visible always (desktop) */}
          <button
            onClick={openUpgrade}
            className="hidden md:inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-display font-semibold btn-primary"
            data-testid="btn-header-upgrade"
            title={t("header_upgrade")}
          >
            <Zap size={12} /> {t("header_upgrade")}
          </button>

          {/* Earn Credits — primary CTA for the CPX Research survey rail.
              Replaces the old AdSense rewarded slot. Hidden when surveys
              are disabled or the user has hit today's daily cap. */}
          {rewardsEnabled && (
          <button
            onClick={onOpenAd}
            className="hidden md:inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-display font-semibold border border-[#00FF9D]/40 bg-[#00FF9D]/[0.08] text-[#00FF9D] hover:bg-[#00FF9D]/[0.16] transition"
            data-testid="btn-header-earn-credits"
            title={t("nav_earn_credits") || "Earn Credits"}
          >
            <Gift size={12} /> {t("nav_earn_credits") || "Earn Credits"}
          </button>
          )}

          {/* Focus Streak (hidden if 0) */}
          <FocusStreak />

          {/* Admin-only revenue pill — launch-day motivator */}
          {adminStats?.is_admin && (
            <Link
              to="/admin/revenue"
              className="hidden sm:flex items-center gap-1.5 rounded-full pl-2.5 pr-3 py-1 text-sm glass !rounded-full border-[#00FF9D]/30 bg-[#00FF9D]/[0.04] hover:bg-[#00FF9D]/[0.08] transition group"
              title={`Launch-day revenue · ${adminStats.today_orders || 0} order${adminStats.today_orders === 1 ? "" : "s"} today · click for full dashboard`}
              data-testid="header-admin-revenue"
            >
              <span className="text-[#00FF9D] font-display font-bold text-[12px]">₹</span>
              <span className="font-display font-semibold text-sm tabular-nums">
                {(adminStats.today_inr ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
              <span className="text-white/45 text-[10px] tracking-wider uppercase hidden md:inline">today</span>
            </Link>
          )}

          {/* Menu dropdown — `shrink-0` is critical: on narrow Android
              viewports (Realme GT 7 ≈ 412px) the row used to overflow
              and push this button off the right edge of the screen,
              making the menu inaccessible. shrink-0 + a sticky relative
              ancestor guarantees the hamburger always renders. */}
          <div ref={menuRef} className="relative shrink-0">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="h-9 w-9 grid place-items-center rounded-full border border-white/10 hover:border-white/30 text-white/75 hover:text-white transition"
              data-testid="btn-menu"
              aria-label="Menu"
            >
              {menuOpen ? <X size={16} /> : <Menu size={16} />}
            </button>

            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-60 rounded-2xl p-1.5 shadow-2xl shadow-black/60 z-50 border border-white/10"
                style={{ backgroundColor: "#0A192F" }}
                data-testid="header-menu"
              >
                {user && (
                  <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/5 mb-1">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-white/10" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-white/10 grid place-items-center text-xs font-semibold">
                        {(user.name || "U").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-display text-white truncate">{user.name}</div>
                      <div className="text-[11px] text-white/45 truncate">{user.email}</div>
                    </div>
                  </div>
                )}

                {/* Continue where you left off — appears as the top item
                    in the hamburger whenever the user has at least one
                    generated path. Pulls /counselor/resume which returns
                    the most recently active path (scroll pings → falls
                    back to last_viewed_at → created_at) + a progress
                    estimate. When the user has actually scrolled the
                    path, we display a precise percentage; otherwise we
                    show the coarse "Step N/M" derived from how many
                    module parts have been cached. */}
                {user && resume && resume.path_id && (
                  <Link
                    to={`/path/${resume.path_id}`}
                    onClick={() => setMenuOpen(false)}
                    className="block mb-1 rounded-xl border border-[#00FF9D]/30 bg-gradient-to-br from-[#00FF9D]/[0.08] to-[#0044FF]/[0.04] hover:from-[#00FF9D]/[0.14] hover:to-[#0044FF]/[0.10] transition group"
                    data-testid="menu-resume-card"
                  >
                    <div className="px-3 py-2.5">
                      <div className="flex items-center gap-2 mb-1.5">
                        <PlayCircle size={13} className="text-[#00FF9D] shrink-0" />
                        <span className="text-[10px] font-display uppercase tracking-[0.16em] text-[#9bffcc]">
                          Continue where you left off
                        </span>
                      </div>
                      <div
                        className="text-[13px] text-white font-display leading-snug line-clamp-2 group-hover:text-white"
                        data-testid="menu-resume-goal"
                      >
                        {resume.goal || "Your last path"}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1 rounded-full bg-white/8 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-[#00FF9D] to-[#0044FF] transition-all"
                            style={{ width: `${Math.round((resume.progress_pct || 0) * 100)}%` }}
                            data-testid="menu-resume-progress-bar"
                          />
                        </div>
                        <span
                          className="text-[10px] font-mono-oak text-white/55 tabular-nums whitespace-nowrap"
                          data-testid="menu-resume-progress-text"
                        >
                          {resume.scroll_derived
                            ? `${Math.round((resume.progress_pct || 0) * 100)}% read`
                            : `Step ${Math.min((resume.step_index ?? 0) + 1, resume.total_steps || 5)}/${resume.total_steps || 5}`}
                        </span>
                      </div>
                    </div>
                  </Link>
                )}

                {MENU_ITEMS.map((it) => (
                  <Link
                    key={it.to}
                    to={it.to}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-white/75 hover:text-white hover:bg-white/5 transition"
                    data-testid={`menu-${it.to.replace("/", "")}`}
                  >
                    <it.Icon size={15} className="text-white/45" />
                    {t(it.key)}
                  </Link>
                ))}

                {/* My Courses — expandable submenu listing every saved
                    path. Click a row → /dashboard?path=<id> which the
                    Dashboard reads on mount and pre-selects the path
                    in the BentoPath. Lazy-loaded on first open. */}
                {user && (
                  <>
                    <button
                      type="button"
                      onClick={expandMyCourses}
                      className="w-full flex items-center justify-between gap-2.5 px-3 py-2.5 rounded-xl text-sm text-white/75 hover:text-white hover:bg-white/5 transition"
                      data-testid="menu-my-courses"
                      aria-expanded={myCoursesOpen}
                    >
                      <span className="flex items-center gap-2.5">
                        <BookOpen size={15} className="text-[#ffb067]" />
                        {t("nav_my_courses") || "My Courses"}
                        {Array.isArray(myCourses) && (
                          <span className="text-[10px] text-white/45 font-mono-oak tabular-nums">
                            {myCourses.length}
                          </span>
                        )}
                      </span>
                      {myCoursesOpen ? <ChevronDown size={14} className="text-white/45" /> : <ChevronRight size={14} className="text-white/45" />}
                    </button>

                    {myCoursesOpen && (
                      <div
                        className="ml-2 pl-2 border-l border-white/8 max-h-72 overflow-y-auto"
                        data-testid="my-courses-list"
                      >
                        {myCoursesLoading ? (
                          <div className="px-2 py-2 text-[12px] text-white/45">{t("loading") || "Loading…"}</div>
                        ) : !myCourses || myCourses.length === 0 ? (
                          <div className="px-2 py-2 text-[12px] text-white/45">
                            {t("my_courses_empty") || "No courses yet — generate one from the dashboard."}
                          </div>
                        ) : (
                          myCourses.map((p) => (
                            <Link
                              key={p.path_id}
                              to={`/path/${p.path_id}`}
                              onClick={() => setMenuOpen(false)}
                              className="block px-3 py-2 rounded-lg text-[12.5px] text-white/75 hover:text-white hover:bg-white/5 transition truncate"
                              title={p.goal}
                              data-testid="my-courses-item"
                            >
                              {p.goal}
                            </Link>
                          ))
                        )}
                      </div>
                    )}
                  </>
                )}

                {rewardsEnabled && (
                <button
                  onClick={() => { setMenuOpen(false); onOpenAd?.(); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-display font-semibold text-[#00FF9D] hover:bg-[#00FF9D]/10 transition"
                  data-testid="menu-earn-credits"
                >
                  <Gift size={15} className="text-[#00FF9D]" /> {t("nav_earn_credits") || "Earn Credits"}
                </button>
                )}

                <button
                  onClick={() => { setMenuOpen(false); openUpgrade(); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-white/75 hover:text-white hover:bg-white/5 transition"
                  data-testid="menu-upgrade"
                >
                  <CreditCard size={15} className="text-[#8aa8ff]" /> {t("header_get_credits")}
                </button>

                <div className="border-t border-white/5 my-1" />

                {/* Accessibility toggles — surfaced inside the hamburger
                    on small Android phones (sm:hidden in the header bar
                    means these buttons are otherwise invisible there).
                    Replicates the Type + Focus toggles 1:1 so the user
                    never has to leave the page to flip a mode. */}
                <div className="sm:hidden flex items-center gap-2 px-3 py-2" data-testid="menu-accessibility-row">
                  <button
                    type="button"
                    onClick={() => {
                      const next = !dyslexia;
                      setDyslexia(next);
                      trackEvent("accessibility_toggle", { feature: "opendyslexic", enabled: next });
                    }}
                    className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-display font-semibold border transition ${
                      dyslexia
                        ? "border-[#00FF9D]/60 text-[#00FF9D] bg-[#00FF9D]/10"
                        : "border-white/10 text-white/60 hover:text-white hover:border-white/30"
                    }`}
                    aria-pressed={dyslexia}
                    data-testid="menu-toggle-dyslexia"
                  >
                    <Type size={13} /> {dyslexia ? "Reader On" : "Reader"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const next = !focusMode;
                      setFocusMode(next);
                      trackEvent("accessibility_toggle", { feature: "focus_mode", enabled: next });
                    }}
                    className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-display font-semibold border transition ${
                      focusMode
                        ? "border-[#8aa8ff]/60 text-[#8aa8ff] bg-[#8aa8ff]/10"
                        : "border-white/10 text-white/60 hover:text-white hover:border-white/30"
                    }`}
                    aria-pressed={focusMode}
                    data-testid="menu-toggle-focus"
                  >
                    <Eye size={13} /> {focusMode ? "Focus On" : "Focus"}
                  </button>
                </div>

                {adminStats?.is_admin && (
                  <Link
                    to="/admin/revenue"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-display font-semibold text-[#00FF9D] hover:bg-[#00FF9D]/10 transition"
                    data-testid="menu-admin-revenue"
                  >
                    <ShieldCheck size={15} className="text-[#00FF9D]" /> Revenue Dashboard
                  </Link>
                )}
                <Link
                  to="/settings"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-white/75 hover:text-white hover:bg-white/5 transition"
                  data-testid="menu-profile"
                >
                  <User size={15} className="text-white/45" /> Profile
                </Link>
                <Link
                  to="/settings"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-white/75 hover:text-white hover:bg-white/5 transition"
                  data-testid="menu-settings-bottom"
                >
                  <SettingsIcon size={15} className="text-white/45" /> Settings
                </Link>
                <button
                  onClick={() => { setMenuOpen(false); logout(); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-rose-300 hover:bg-rose-500/10 transition"
                  data-testid="menu-logout"
                >
                  <LogOut size={15} /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
